import type * as Ponyfill from 'barcode-detector/ponyfill';
import wasmAsset from 'zxing-wasm/reader/zxing_reader.wasm?url';

/**
 * Decoding a pass symbol from a camera frame or a photograph, through the W3C `BarcodeDetector`.
 *
 * **The application targets the standard, not a library.** Chrome, Edge and Opera implement
 * `BarcodeDetector` natively; Firefox and Safari do not. So this module asks the platform first and
 * falls back to the `barcode-detector` ponyfill, which is the same API implemented over a
 * WebAssembly build of ZXing. Everything above sees one interface either way, and a browser that
 * grows native support stops downloading a megabyte of WebAssembly with no code change.
 *
 * **The ponyfill rather than the polyfill**, which is the same decision for the same reason as
 * everywhere else in this repository: installing `globalThis.BarcodeDetector` would make "is this
 * browser capable" a question whose answer depends on whether this module has been imported yet,
 * and a feature check elsewhere would then be reading our own footprint.
 *
 * **The WebAssembly asset is served from our own origin, and that is not a preference.** The
 * library's default `locateFile` fetches it from a public CDN; Section 1.10's
 * `Content-Security-Policy` is `default-src 'self'` with no CDN anywhere, so the default is refused
 * by the browser — and a gate terminal on a facility's own network has no route to the internet at
 * all, so a scanner that needed one would work in development and fail on the day it was installed.
 * {@link wasmAssetPath} is therefore root-relative by construction, and a test asserts it can never
 * name a host.
 *
 * **The module is imported dynamically**, so the decoder's own code is fetched when an operator
 * opens the camera rather than when the application starts. The hardware wedge is the primary way a
 * pass is read (AD-32); the camera is the fallback for a terminal with no scanner, and on most
 * shifts nobody opens it.
 */

/** What a decode found. Narrower than `DetectedBarcode`: nothing above needs the geometry. */
interface DetectedCode {
  readonly rawValue: string;
}

/** The slice of `BarcodeDetector` this application uses. */
interface CodeDetector {
  detect: (source: ImageBitmapSource) => Promise<readonly DetectedCode[]>;
}

/** The one format this product encodes, and therefore the only one worth asking a decoder for. */
const PASS_BARCODE_FORMAT = 'qr_code';

/** Where the decoder came from, which is worth knowing when a terminal decodes slowly. */
type DetectorSource = 'native' | 'wasm';

export interface LoadedDetector {
  readonly detector: CodeDetector;
  readonly source: DetectorSource;
}

/** This browser cannot decode a symbol at all, which is a state the operator is told about. */
class DecoderUnavailableError extends Error {
  constructor(reason: string, options?: { cause?: unknown }) {
    super(`this browser cannot decode a pass symbol: ${reason}`);
    this.name = 'DecoderUnavailableError';
    this.cause = options?.cause;
  }
}

/**
 * Where the WebAssembly module is fetched from.
 *
 * **Imported as a URL, so it goes through the build like every other asset**: Rollup emits it with
 * a content hash, the budget plugin counts it, and a version bump changes the file name so no
 * browser can serve a stale decoder out of its cache. The alternative — copying the file into the
 * output beside the build — would put half a megabyte outside everything that measures what this
 * application ships.
 *
 * **Root-relative by construction, and a test pins that.** The URL Vite produces is relative to the
 * deployment root, so it is correct behind any host name and under TLS with nothing configured, and
 * it can never name another origin. That is the whole of "no CDN request is made for the wasm
 * asset": the library's own default fetches it from a public CDN, which this product's CSP forbids
 * and which a gate terminal on a facility network cannot reach at all.
 */
export const WASM_ASSET_URL: string = wasmAsset;

/**
 * The `locateFile` this module installs, exported so a test can assert what it answers.
 *
 * It ignores the name it is given. Emscripten asks for the file by the name it was built under and
 * the build emits it under a hashed one, so the URL the bundler produced is the only correct
 * answer; deriving one from the argument would fetch a file that is not there.
 */
export function locateWasmAsset(): string {
  return WASM_ASSET_URL;
}

/**
 * The ponyfill's own shape, imported as a TYPE so the module itself stays out of the eager bundle.
 *
 * A type-only import is erased entirely, so this costs nothing at run time and the one `import()`
 * below is still the only thing that fetches the decoder.
 */
type PonyfillModule = typeof Ponyfill;

/** The shape of a `BarcodeDetector` constructor, native or ponyfilled. */
export interface DetectorConstructor {
  new (options?: { formats?: readonly string[] }): CodeDetector;
  getSupportedFormats?: () => Promise<readonly string[]>;
}

/** The global as this module reads it, so a test can hand in one that has or lacks the API. */
export interface DetectorScope {
  readonly BarcodeDetector?: unknown;
}

async function nativeConstructor(scope: DetectorScope): Promise<DetectorConstructor | null> {
  const candidate = scope.BarcodeDetector;
  if (typeof candidate !== 'function') {
    return null;
  }
  const constructor = candidate as DetectorConstructor;
  try {
    const formats = await constructor.getSupportedFormats?.();
    // A native implementation that cannot read a QR symbol is no use here, and it exists: several
    // platforms ship the API with a format list that depends on the underlying system library.
    // Falling through to the WebAssembly build is better than reporting the terminal as capable.
    return formats === undefined || formats.includes(PASS_BARCODE_FORMAT) ? constructor : null;
  } catch {
    return null;
  }
}

/**
 * The WebAssembly decoder, with its asset pointed at THIS origin.
 *
 * **Exported so the one line that does that can be asserted.** `prepareZXingModule` appears exactly
 * once in this repository, here, and every test injects {@link loadCodeDetector}'s `fallback`
 * instead — so deleting it turned nothing red while every Firefox and Safari terminal silently fell
 * back to the library's default `locateFile`, which fetches from a public CDN that this product's
 * `connect-src 'self'` forbids and a facility network cannot reach at all. The failure is silent:
 * the camera panel simply never finds anything.
 *
 * `load` is a seam for the same reason: the module cannot be imported in the `web` tier without
 * pulling in the real WebAssembly, and what has to be proved is what this function DOES with it.
 */
export async function wasmConstructor(
  load: () => Promise<PonyfillModule> = async () => import('barcode-detector/ponyfill'),
): Promise<DetectorConstructor> {
  let module: PonyfillModule;
  try {
    module = await load();
  } catch (error) {
    throw new DecoderUnavailableError('the decoder could not be loaded', { cause: error });
  }
  // Before anything is decoded, or the first decode fetches the asset from the library's default
  // CDN, which this product's CSP forbids and an offline gate cannot reach.
  module.prepareZXingModule({ overrides: { locateFile: locateWasmAsset } });
  return module.BarcodeDetector as unknown as DetectorConstructor;
}

/**
 * The decoder this browser can offer, or a refusal saying it has none.
 *
 * **Not cached here.** The camera panel holds the instance it opened for as long as it is scanning,
 * which is the lifetime that matters; a module-level cache would keep a WebAssembly instance and
 * its heap alive for the whole shift after one photograph was decoded.
 *
 * **Both seams are arguments so the unit tier can exercise the CHOICE without the fallback.** The
 * platform is handed in so a test can present a browser with a native decoder, with a native
 * decoder that cannot read a QR symbol, or with none at all; and the fallback is handed in because
 * instantiating the real one means fetching a megabyte of WebAssembly, which that tier blocks on
 * purpose. What is under test here is which of the two is chosen, and that is decided before either
 * runs.
 */
export async function loadCodeDetector(
  scope?: DetectorScope,
  fallback: () => Promise<DetectorConstructor> = wasmConstructor,
): Promise<LoadedDetector> {
  const platform: DetectorScope = scope ?? (globalThis as DetectorScope);
  const native = await nativeConstructor(platform);
  if (native !== null) {
    return { detector: new native({ formats: [PASS_BARCODE_FORMAT] }), source: 'native' };
  }
  const wasm = await fallback();
  return { detector: new wasm({ formats: [PASS_BARCODE_FORMAT] }), source: 'wasm' };
}
